import type { Coordinates } from "@hopper-b2b/types";

const GOOGLE_MAP_URL = `https://www.google.com/maps/search/?api=1`;

export type OpenMapProps = {
  placeId?: string;
  coordinates?: Coordinates;
  address?: string;
  placeName?: string;
  zoom?: number;
};

/*
  Use placeId if provided (most accurate)
  Otherwise, combine placename/address with coordinates for disambiguation
  Otherwise, use placename and address alone
  Otherwise, use coordinates alone
*/
export const getGoogleMapLink = ({
  placeId,
  coordinates,
  address,
  placeName,
  zoom,
}: OpenMapProps) => {
  // use the placeId if provided, which is most accurate
  if (placeId != null) return `${GOOGLE_MAP_URL}&query_place_id=${placeId}`;

  if (!coordinates && address == null && placeName == null)
    return GOOGLE_MAP_URL;

  const params = new URLSearchParams();

  // Build text query from placeName and address
  const textQuery =
    placeName != null || address != null
      ? [placeName, address].filter(Boolean).join(", ")
      : null;

  // Best accuracy: use text query and center map on coordinates
  if (
    coordinates?.lat != null &&
    coordinates?.lon != null &&
    textQuery != null
  ) {
    params.append("query", textQuery);
    // Center the map on the coordinates to disambiguate
    params.append("center", `${coordinates.lat},${coordinates.lon}`);
    if (zoom != null) {
      params.append("zoom", zoom.toString());
    }
    return `${GOOGLE_MAP_URL}&${params.toString()}`;
  }
  // Fallback: text search only
  else if (textQuery != null) {
    params.append("query", textQuery);
    if (zoom != null) {
      params.append("zoom", zoom.toString());
    }
    return `${GOOGLE_MAP_URL}&${params.toString()}`;
  }
  // Fallback: coordinates only
  else if (coordinates?.lat != null && coordinates?.lon != null) {
    params.append("query", `${coordinates.lat},${coordinates.lon}`);
    if (zoom != null) {
      params.append("zoom", zoom.toString());
    }
    return `${GOOGLE_MAP_URL}&${params.toString()}`;
  }

  return GOOGLE_MAP_URL;
};

// Other info will be ignored if placeId is provided
export const openGoogleMap = (props: OpenMapProps) => {
  const newWindow = window.open(
    getGoogleMapLink(props),
    "_blank",
    "noopener,noreferrer"
  );
  if (newWindow) newWindow.opener = null; // Extra security measure for older browsers
};
